<template>
  <div class="one-off-payment">
    <div v-if="loading" class="loader-container">
      <loader />
    </div>

    <div v-else-if="!selectedOffering" class="not-found padding-ml">
      <div class="ri-body">לא הצלחנו למצוא את המוצר המבוקש.</div>
    </div>

    <div v-else-if="done" class="after-payment" :class="{ 'no-details': alreadyPurchased }">
      <dashboard-page :image-header-props="doneImageHeaderProps">
        <template v-slot:header>
          <div class="headline-container padding-top-ml flex-column flex-1">
            <template v-if="alreadyPurchased">
              <div class="ri-large-body">הרכישה כבר בוצעה</div>
              <div class="ri-small-display">לא בוצע חיוב נוסף</div>
            </template>
            <template v-else>
              <div class="ri-large-body">פרטי התשלום התקבלו בהצלחה</div>
              <div class="ri-small-display">
                <div>תודה שבחרת</div>
                <div>לצמוח איתנו</div>
              </div>
            </template>
          </div>
          <div class="header-animation-container">
            <lottie-player src="/animations/payment-success.json" autoplay loop class="success-lottie" />
          </div>
        </template>
        <template v-slot:dashboard>
          <div v-if="!alreadyPurchased" class="margin-ml">
            <property class="margin-bottom-m" name="סוג התוכנית" :value="selectedOffering.name" />
            <property class="margin-bottom-m" name="סך הכל לתשלום" :value="`${price} ₪`" />
            <property class="margin-bottom-m" name="תאריך לחיוב" :value="currentDate" />
            <property class="margin-bottom-m" name="כרטיס אשראי לחיוב" :value="cardMaskDisplay" />
            <property class="margin-bottom-xs" name="אימייל לשליחת קבלות" :value="email" />
          </div>
        </template>
      </dashboard-page>
    </div>

    <header-page v-else :header-props="headerProps">
      <image-header-page :image-header-props="heroImageHeaderProps">
      <div class="content">
        <payment-form-section>
          <div class="hero-title ri-small-display ri-font-weight-bold">{{ selectedOffering.name }}</div>
        </payment-form-section>

        <payment-form-section title="על שם מי הכרטיס?">
          <div class="personal-info-fields">
            <customer-input class="field" :labelText="'שם פרטי'"
                            :value="firstName" :onInput="setFirstName" :has-error="isInvalid(firstName, nameValid)" />
            <customer-input class="field" :labelText="'שם משפחה'"
                            :value="lastName" :onInput="setLastName" :has-error="isInvalid(lastName, nameValid)" />
            <customer-input class="field" :labelText="'מס׳ נייד'"
                            :value="phone" :onInput="setPhone" :has-error="isInvalid(phone, phoneValid)" />
            <customer-input class="field" :labelText="'אימייל'"
                            :value="email" :onInput="setEmail" :has-error="isInvalid(email, emailValid)" />
          </div>
        </payment-form-section>

        <payment-form-section :has-bottom-padding="false">
          <credit-card-details-form @init-failed="onCardInitFailed" />
        </payment-form-section>

        <payment-form-section title="פרטי התשלום" :bottom-separator="false" :has-bottom-padding="false">
          <payment-details :price="price" :payment-date="currentDate" :discount="false" />
          <offering-payment-installments
            v-if="selectedOffering.possibleNumberOfInstallments > 1"
            :namespace="flowConfig.storeNamespace"
            :price="price" />
        </payment-form-section>

        <payment-form-section :bottom-separator="false">
          <div class="consents">
            <check-box class="marketing-consent" :initially-checked="enableMarketingEmails" @input="setMarketingConsent">
              <template v-slot:title>
                <div class="marketing-consent-text">אשמח לקבל עדכונים, טיפים וחדשות</div>
              </template>
            </check-box>
            <check-box class="terms-consent" :initially-checked="termsApproved" @input="setTermsApproved">
              <template v-slot:title>
                <div class="terms-consent-text">
                  קראתי ואישרתי את
                  <span class="terms-link" @click="openTerms">תנאי השימוש בשירות</span>
                </div>
              </template>
            </check-box>
          </div>
        </payment-form-section>

        <payment-form-section :bottom-separator="false">
          <div class="actions">
            <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>
            <riseup-button :title="'אישור ותשלום'" :action="onSubmit" :variant="'primary'"
                           :disabled="!formValid" :loading="submitLoading" />
          </div>
        </payment-form-section>
      </div>
      </image-header-page>
    </header-page>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import moment from 'moment';
import BaseUI from '@riseupil/base-ui';
import { BACKGROUND_POSITION, COLOR_VARIANT } from '@riseupil/base-ui/src/utils/layout-consts';
import Segment from '@/Segment';
import Loader from '@/base-components/Loader';
import LoginApi from '@/api/LoginApi';
import phoneNumberUtils from '@/utils/phoneNumber';
import PaymeInterface from '@/Payme';
import OtpConfirmationPopup from '@/pages/responsive-pages/non-authenticated/offering-payment/OtpConfirmationPopup.vue';
import CustomerInput from '@/base-components/card-parts/CustomerInput.vue';
import CreditCardDetailsForm from '@/pages/responsive-pages/authenticated/payment/CreditCardDetailsForm';
import PaymentDetails from '@/pages/responsive-pages/authenticated/subscription/PaymentDetails.vue';
import OfferingPaymentInstallments from '@/pages/responsive-pages/non-authenticated/offering-payment/OfferingPaymentInstallments.vue';
import PaymentFormSection from '@/pages/responsive-pages/authenticated/navigation-pages/payment/PaymentForm/PaymentFormSection';
import Property from '@/pages/responsive-pages/authenticated/navigation-pages/account-settings/Property.vue';
import creditCardHeaderImg from '@/assets/subscription/credit-card-header.svg';
import { paymentFlowConfig } from './paymentFlows';

const TRANSPARENT_PIXEL = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
const TERMS_URL = 'https://www.riseup.co.il/termsofuse/';

export default {
  name: 'OneOffPaymentPage',
  components: {
    CustomerInput,
    CreditCardDetailsForm,
    PaymentDetails,
    OfferingPaymentInstallments,
    PaymentFormSection,
    Property,
    Loader,
    RiseupButton: BaseUI.RiseupButton,
    CheckBox: BaseUI.CheckBox,
    DashboardPage: BaseUI.Pages.DashboardPage,
    ImageHeaderPage: BaseUI.Pages.ImageHeaderPage,
    HeaderPage: BaseUI.Pages.HeaderPage,
  },
  props: {
    offeringId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      submitLoading: false,
      done: false,
      cardInitFailed: false,
      errorMessage: null,
      firstName: '',
      lastName: '',
      phone: '',
      email: '',
      enableMarketingEmails: false,
      termsApproved: false,
      alreadyPurchased: false,
      cardMask: null,
      paymeToken: null,
    };
  },
  async created() {
    Segment.trackUserGot('LeadOneOffPaymentPage', { offeringId: this.offeringId, flow: this.flowConfig.flow });
    try {
      await this.$store.dispatch(`${this.flowConfig.storeNamespace}/init`, { offeringId: this.offeringId });
    } catch (error) {
      this.errorMessage = 'אירעה שגיאה בטעינת המוצר';
    } finally {
      this.loading = false;
    }
  },
  beforeDestroy() {
    PaymeInterface.teardown();
  },
  computed: {
    ...mapState('subscription', ['fieldsInstance']),
    ...mapGetters('modalRootStore', ['showingModal']),
    flowConfig() {
      return paymentFlowConfig(this.$route.meta.flow);
    },
    selectedOffering() {
      return this.$store.getters[`${this.flowConfig.storeNamespace}/selectedOffering`];
    },
    price() {
      return this.$store.getters[`${this.flowConfig.storeNamespace}/price`];
    },
    currentDate() {
      return moment().format('D.M.YYYY');
    },
    normalizedPhone() {
      return phoneNumberUtils.formatPhoneNumber(this.phone.replace(/\D/g, ''));
    },
    phoneLastDigits() {
      return this.normalizedPhone.slice(-4);
    },
    cardMaskDisplay() {
      if (!this.cardMask) {
        return '';
      }
      const digits = String(this.cardMask).replace(/\D/g, '');
      return `${digits.slice(-4)} ••••`;
    },
    formValid() {
      return this.nameValid(this.firstName)
        && this.nameValid(this.lastName)
        && this.phoneValid(this.phone)
        && this.emailValid(this.email)
        && !this.cardInitFailed
        && !!this.fieldsInstance
        && this.termsApproved;
    },
    heroImageHeaderProps() {
      return {
        backgroundColor: COLOR_VARIANT.BLUE,
        imgSrc: creditCardHeaderImg,
        imgHeight: 130,
        backgroundPosition: BACKGROUND_POSITION.BOTTOM_RIGHT,
        bottomSeparator: false,
      };
    },
    doneImageHeaderProps() {
      return {
        backgroundColor: COLOR_VARIANT.BLUE,
        imgSrc: TRANSPARENT_PIXEL,
        bottomSeparator: false,
        textColor: COLOR_VARIANT.WHITE,
        backgroundPosition: BACKGROUND_POSITION.BOTTOM,
        imgHeight: 471,
      };
    },
    headerProps() {
      return {
        rightActions: [{
          type: BaseUI.HeaderActions.BACK,
          onClick: this.onBack,
        }],
        showLogo: true,
        backgroundColorVariant: COLOR_VARIANT.BLUE,
        logoColor: COLOR_VARIANT.WHITE,
        textColor: COLOR_VARIANT.WHITE,
        bottomBorder: false,
      };
    },
  },
  methods: {
    ...mapActions('subscription', ['createLeadPaymentToken']),
    ...mapActions('modalRootStore', ['openModal']),
    onBack() {
      if (this.$listeners.back) {
        this.$emit('back');
      } else {
        this.$router.push({ path: this.flowConfig.routeBase });
      }
    },
    nameValid(value) {
      return !!value && value.length >= 2;
    },
    phoneValid(value) {
      return !!value && value.replace(/\D/g, '').length >= 10;
    },
    emailValid(value) {
      return /.+@.+\..+/.test(value);
    },
    isInvalid(value, validator) {
      return !!value && !validator(value);
    },
    setFirstName(value) {
      this.firstName = value;
    },
    setLastName(value) {
      this.lastName = value;
    },
    setPhone(value) {
      this.phone = value;
    },
    setEmail(value) {
      this.email = value;
    },
    setMarketingConsent(checked) {
      this.enableMarketingEmails = checked;
    },
    setTermsApproved(checked) {
      this.termsApproved = checked;
    },
    openTerms() {
      window.open(TERMS_URL, '_blank');
    },
    onCardInitFailed() {
      this.cardInitFailed = true;
      this.errorMessage = 'נראה שיש בעיה עם מערכת התשלום. אפשר לרענן ולנסות שוב';
    },
    async onSubmit() {
      if (this.submitLoading || this.showingModal) {
        return;
      }
      this.submitLoading = true;
      this.errorMessage = null;
      Segment.trackUserInteraction('LeadPaymentClicked', { offeringId: this.offeringId, flow: this.flowConfig.flow });
      try {
        this.paymeToken = await this.createLeadPaymentToken({
          firstName: this.firstName,
          lastName: this.lastName,
          phone: this.phone,
          email: this.email,
          price: this.price,
        });
        this.cardMask = this.paymeToken.card && this.paymeToken.card.cardMask;
      } catch (error) {
        Segment.trackUserGot('LeadPurchaseFailed', { offeringId: this.offeringId, flow: this.flowConfig.flow });
        this.errorMessage = 'התשלום נכשל. אפשר לבדוק את פרטי הכרטיס ולנסות שוב';
        this.submitLoading = false;
        return;
      }
      try {
        await LoginApi.generateCode({ phoneNumber: this.normalizedPhone });
        Segment.trackUserGot('LeadOtpSent', { offeringId: this.offeringId, flow: this.flowConfig.flow, phoneLastDigits: this.phoneLastDigits });
        this.openModal({
          component: OtpConfirmationPopup,
          popupAlignment: 'center',
          props: {
            phoneNumber: this.phone,
            confirmCode: this.purchaseWithOtp,
            resendCode: this.resendOtp,
          },
        });
      } catch (error) {
        Segment.trackUserGot('LeadOtpSendFailed', { offeringId: this.offeringId, flow: this.flowConfig.flow, phoneLastDigits: this.phoneLastDigits });
        this.errorMessage = 'לא הצלחנו לשלוח קוד אימות למספר הזה. כדאי לבדוק את המספר ולנסות שוב';
      } finally {
        this.submitLoading = false;
      }
    },
    async resendOtp(sendPlatform) {
      await LoginApi.generateCode({ phoneNumber: this.normalizedPhone, force: true, sendPlatform });
    },
    async purchaseWithOtp(otpCode) {
      try {
        const result = await this.$store.dispatch(`${this.flowConfig.storeNamespace}/purchaseLead`, {
          paymeToken: this.paymeToken,
          phoneNumber: this.normalizedPhone,
          firstName: this.firstName,
          lastName: this.lastName,
          email: this.email,
          enableMarketingEmails: this.enableMarketingEmails,
          otpCode,
        });
        this.alreadyPurchased = !!(result && result.alreadyPurchased);
        this.done = true;
      } catch (error) {
        if (error && error.message === 'invalid_otp') {
          Segment.trackUserGot('LeadOtpVerifyFailed', {
            offeringId: this.offeringId, flow: this.flowConfig.flow, phoneLastDigits: this.phoneLastDigits,
          });
        } else {
          Segment.trackUserGot('LeadPurchaseFailed', { offeringId: this.offeringId, flow: this.flowConfig.flow });
          this.errorMessage = 'התשלום נכשל. אפשר לבדוק את פרטי הכרטיס ולנסות שוב';
        }
        throw error;
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import '~@riseupil/base-ui/src/scss/riseup-colors';

.one-off-payment {
  height: 100%;
  direction: rtl;

  .loader-container {
    display: flex;
    justify-content: center;
    padding: 40px;
  }

  .content {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    text-align: right;

    .actions {
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;
    }

    .error-message {
      color: $riseup_danger_red;
      font-weight: bold;
    }

    .consents {
      display: flex;
      flex-direction: column;
      gap: 8px;
      width: 100%;
    }

    .marketing-consent-text {
      margin-right: 12px;
    }

    .terms-consent-text {
      margin-right: 12px;

      .terms-link {
        text-decoration: underline;
        cursor: pointer;
        color: $riseup_blue;
      }
    }

  }

  .after-payment {
    text-align: start;

    .headline-container {
      text-align: start;
      row-gap: 9px;
    }

    .header-animation-container {
      position: absolute;
      padding-top: 90px;
    }

    .success-lottie {
      height: 404px;
    }

    ::v-deep .title.with-overlay {
      align-items: unset;
    }

    &.no-details {
      min-height: 100%;
      background-color: $riseup_blue;

      ::v-deep .header-card {
        display: none;
      }
    }
  }
}
</style>
